import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Eye, EyeOff, LockKeyhole } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { paymentApi, usageApi, imageApi, authApi, galleryApi, userApi, activationApi, inviteApi } from '../api/client'
import ShareCodeModal from '../components/ShareCodeModal'
import { isValidActivationCode, isValidShareCode, formatActivationCode } from '../utils/validation'

function toBeijingDateString(ts) {
  return new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(ts).replace(/\//g, '-')
}

function PasswordField({ id, label, value, onChange, visible, onToggle, autoComplete }) {
  const VisibilityIcon = visible ? EyeOff : Eye
  return (
    <label htmlFor={id} className="block">
      <span className="mb-1.5 block text-sm font-medium text-cocoa">{label}</span>
      <span className="relative block">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          minLength={6}
          autoComplete={autoComplete}
          className="form-input pr-11"
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-1.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-cocoa-light transition hover:bg-cream-100 hover:text-cocoa"
          aria-label={visible ? `隐藏${label}` : `显示${label}`}
          title={visible ? '隐藏密码' : '显示密码'}
        >
          <VisibilityIcon size={17} aria-hidden="true" />
        </button>
      </span>
    </label>
  )
}

export default function Profile() {
  const { user, isPremium, logout, loading, refreshUsage, updateUser } = useAuth()
  const [orders, setOrders] = useState([])
  const [ordersLoading, setOrdersLoading] = useState(true)
  const [ordersPage, setOrdersPage] = useState(1)
  const [ordersTotal, setOrdersTotal] = useState(0)
  const ORDERS_PAGE_SIZE = 5

  const [checkinLoading, setCheckinLoading] = useState(false)
  const [checkinMsg, setCheckinMsg] = useState('')
  const [checkinDates, setCheckinDates] = useState([])
  const [checkinCalendarDate, setCheckinCalendarDate] = useState(new Date())

  const [history, setHistory] = useState([])
  const [historyLoading, setHistoryLoading] = useState(true)

  const [patterns, setPatterns] = useState([])
  const [patternsLoading, setPatternsLoading] = useState(true)
  // 分页：我的图纸 / AI 生成历史
  const [patternsPage, setPatternsPage] = useState(1)
  const [historyPage, setHistoryPage] = useState(1)
  const GALLERY_PAGE_SIZE = 9
  const [shareCode, setShareCode] = useState('')
  const [redeemCode, setRedeemCode] = useState('')
  const [redeemLoading, setRedeemLoading] = useState(false)

  // 卡密兑换
  const [activationCode, setActivationCode] = useState('')
  const [activationLoading, setActivationLoading] = useState(false)
  const [activationMsg, setActivationMsg] = useState('')

  // 邀请好友（仅永久会员）
  const isPermanent = Boolean(user && Number(user.premiumUntil) >= 9999999999999)
  const [invites, setInvites] = useState(null)
  const [inviteLoading, setInviteLoading] = useState(false)
  const [inviteMsg, setInviteMsg] = useState('')

  const [editingUsername, setEditingUsername] = useState(false)
  const [usernameDraft, setUsernameDraft] = useState(user?.username || '')
  const [usernameError, setUsernameError] = useState('')
  const [usernameSaving, setUsernameSaving] = useState(false)

  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' })
  const [passwordVisible, setPasswordVisible] = useState({ current: false, next: false, confirm: false })
  const [passwordSaving, setPasswordSaving] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [passwordMessage, setPasswordMessage] = useState('')

  const fetchOrders = (page = 1) => {
    setOrdersLoading(true)
    paymentApi
      .getOrders(page, ORDERS_PAGE_SIZE)
      .then(({ data }) => {
        setOrders(data.orders || [])
        setOrdersTotal(data.total || 0)
        setOrdersPage(data.page || page)
      })
      .catch(() => {
        setOrders([])
        setOrdersTotal(0)
      })
      .finally(() => setOrdersLoading(false))
  }

  const fetchCheckins = (year, month) => {
    usageApi
      .getCheckins(year, month)
      .then(({ data }) => setCheckinDates(data.dates || []))
      .catch(() => setCheckinDates([]))
  }

  useEffect(() => {
    if (!user) return
    fetchOrders(1)
    const now = new Date()
    fetchCheckins(now.getFullYear(), now.getMonth() + 1)

    imageApi
      .history()
      .then(({ data }) => setHistory(data.history || []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false))

    setPatternsLoading(true)
    userApi
      .get(user.id)
      .then(({ data }) => setPatterns(data.items || []))
      .catch(() => setPatterns([]))
      .finally(() => setPatternsLoading(false))
  }, [user])

  useEffect(() => {
    if (!user) return
    fetchOrders(ordersPage)
  }, [ordersPage])

  useEffect(() => {
    if (!user || !isPermanent) return
    inviteApi
      .list()
      .then(({ data }) => setInvites(data))
      .catch(() => setInvites(null))
  }, [user, isPermanent])

  const handleInviteCreate = async () => {
    setInviteLoading(true)
    setInviteMsg('')
    try {
      const { data } = await inviteApi.create()
      setInvites(data)
      setInviteMsg('✅ 邀请码已生成，快发给好友吧~')
    } catch (err) {
      setInviteMsg(`❌ ${err.response?.data?.error || '生成失败'}`)
    } finally {
      setInviteLoading(false)
    }
  }

  const handleInviteCopy = async (code) => {
    try {
      await navigator.clipboard.writeText(code)
      setInviteMsg('✅ 已复制到剪贴板')
    } catch {
      setInviteMsg(`请手动复制：${code}`)
    }
  }

  const handleCheckin = async () => {
    setCheckinLoading(true)
    setCheckinMsg('')
    try {
      const { data } = await usageApi.checkin()
      setCheckinMsg(data.message)
      await refreshUsage()
      const d = checkinCalendarDate
      fetchCheckins(d.getFullYear(), d.getMonth() + 1)
    } catch (err) {
      setCheckinMsg(err.response?.data?.error || '签到失败')
    } finally {
      setCheckinLoading(false)
    }
  }

  const togglePatternVisibility = async (item) => {
    try {
      const { data } = await galleryApi.setVisibility(item.id, !item.isPublic)
      setPatterns((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, isPublic: data.isPublic } : i))
      )
    } catch (err) {
      alert(err.response?.data?.error || '操作失败')
    }
  }

  const handleSharePattern = async (item) => {
    try {
      const { data } = await galleryApi.createShareCode(item.id)
      setShareCode(data.shareCode)
      setPatterns((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, shareCode: data.shareCode } : i))
      )
    } catch (err) {
      alert(err.response?.data?.error || '生成分享码失败')
    }
  }

  const handleRedeemCode = async () => {
    const code = redeemCode.trim().toUpperCase()
    if (!isValidShareCode(code)) return
    setRedeemLoading(true)
    try {
      const { data } = await galleryApi.getByShareCode(code)
      window.location.href = `/gallery/${data.id}?code=${encodeURIComponent(code)}`
    } catch (err) {
      alert(err.response?.data?.error || '分享码无效或已失效')
    } finally {
      setRedeemLoading(false)
    }
  }

  // 卡密兑换
  const handleActivationRedeem = async () => {
    const code = activationCode.trim().toUpperCase()
    if (!isValidActivationCode(code)) {
      setActivationMsg('❌ 卡密格式为 XXXX-XXXX-XXXX-XXXX')
      return
    }
    setActivationLoading(true)
    setActivationMsg('')
    try {
      const { data } = await activationApi.redeem(code)
      setActivationMsg(`✅ ${data.message}`)
      setActivationCode('')
      await refreshUsage()
    } catch (err) {
      setActivationMsg(`❌ ${err.response?.data?.error || '兑换失败'}`)
    } finally {
      setActivationLoading(false)
    }
  }

  const handlePasswordChange = async (event) => {
    event.preventDefault()
    setPasswordError('')
    setPasswordMessage('')

    if (!passwordForm.current) {
      setPasswordError('请输入当前密码')
      return
    }
    if (passwordForm.next.length < 6) {
      setPasswordError('新密码至少需要 6 个字符')
      return
    }
    if (passwordForm.next !== passwordForm.confirm) {
      setPasswordError('两次输入的新密码不一致')
      return
    }
    if (passwordForm.current === passwordForm.next) {
      setPasswordError('新密码不能与当前密码相同')
      return
    }

    setPasswordSaving(true)
    try {
      const { data } = await authApi.changePassword(passwordForm.current, passwordForm.next)
      setPasswordForm({ current: '', next: '', confirm: '' })
      setPasswordVisible({ current: false, next: false, confirm: false })
      setPasswordMessage(data.message || '密码已更新')
    } catch (err) {
      setPasswordError(err.response?.data?.error || '修改失败，请稍后重试')
    } finally {
      setPasswordSaving(false)
    }
  }

  const changeCheckinMonth = (delta) => {
    setCheckinCalendarDate((prev) => {
      const next = new Date(prev)
      next.setMonth(next.getMonth() + delta)
      fetchCheckins(next.getFullYear(), next.getMonth() + 1)
      return next
    })
  }

  const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate()
  const getFirstWeekday = (year, month) => {
    const day = new Date(year, month - 1, 1).getDay()
    return day === 0 ? 6 : day - 1
  }
  const formatDateStr = (year, month, day) =>
    `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
  const isSameDate = (year, month, day) => {
    const now = new Date()
    return now.getFullYear() === year && now.getMonth() + 1 === month && now.getDate() === day
  }
  const isPastDate = (year, month, day) => {
    const now = new Date()
    const target = new Date(year, month - 1, day)
    return target < new Date(now.getFullYear(), now.getMonth(), now.getDate())
  }

  const statusBadge = (status) => {
    if (status === 'paid' || status === 'success') {
      return <span className="rounded-full bg-mint/40 px-2.5 py-0.5 text-xs font-bold text-mint-dark">已支付</span>
    }
    if (status === 'cancelled') {
      return <span className="rounded-full bg-cream-200 px-2.5 py-0.5 text-xs font-bold text-cocoa-muted">已取消</span>
    }
    return <span className="rounded-full bg-peach/40 px-2.5 py-0.5 text-xs font-bold text-cocoa">待支付</span>
  }

  const checkedInToday = Boolean(
    user?.lastCheckinAt &&
      toBeijingDateString(Number(user.lastCheckinAt)) === toBeijingDateString(Date.now())
  )

  if (loading) {
    return <div className="py-20 text-center text-cocoa-light">加载中…</div>
  }

  if (!user) {
    return (
      <div className="mx-auto max-w-md text-center">
        <div className="card py-12">
          <div className="mb-3 text-4xl">🍡</div>
          <h1 className="text-xl font-bold text-cocoa">请先登录</h1>
          <p className="mt-2 text-sm text-cocoa-light">登录豆田造物后查看订单与会员状态</p>
          <a href="/login" className="btn-primary mt-5 inline-block">
            去登录
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* 用户信息 */}
      <div className="card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-blush-light text-3xl shadow-soft">🧁</div>
            <div>
              <h1 className="text-2xl font-bold text-cocoa">个人中心</h1>
              {editingUsername ? (
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    value={usernameDraft}
                    onChange={(e) => {
                      setUsernameDraft(e.target.value)
                      setUsernameError('')
                    }}
                    maxLength={20}
                    className="form-input h-8 max-w-[160px] py-1 text-sm"
                    placeholder="用户名"
                  />
                  <button
                    onClick={async () => {
                      const val = usernameDraft.trim()
                      if (!val || val.length < 2) {
                        setUsernameError('至少 2 个字符')
                        return
                      }
                      setUsernameSaving(true)
                      try {
                        const { data } = await authApi.updateProfile(val)
                        updateUser(data.user)
                        setEditingUsername(false)
                      } catch (err) {
                        setUsernameError(err.response?.data?.error || '保存失败')
                      } finally {
                        setUsernameSaving(false)
                      }
                    }}
                    disabled={usernameSaving}
                    className="rounded-full bg-mint px-3 py-1 text-xs font-semibold text-cocoa shadow-soft transition hover:bg-mint-dark disabled:opacity-60"
                  >
                    {usernameSaving ? '保存中' : '保存'}
                  </button>
                  <button
                    onClick={() => {
                      setEditingUsername(false)
                      setUsernameDraft(user?.username || '')
                      setUsernameError('')
                    }}
                    className="rounded-full border border-lavender/60 px-3 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30"
                  >
                    取消
                  </button>
                  {usernameError && <span className="text-xs text-red-500">{usernameError}</span>}
                </div>
              ) : (
                <p className="mt-0.5 text-cocoa-light">
                  {user.username ? (
                    <>
                      <span className="font-medium text-cocoa">{user.username}</span>
                      <button
                        onClick={() => setEditingUsername(true)}
                        className="ml-1 text-[10px] text-blush-dark hover:underline"
                      >
                        编辑
                      </button>
                      <span className="mx-1">·</span>
                    </>
                  ) : (
                    <button
                      onClick={() => setEditingUsername(true)}
                      className="text-xs text-blush-dark hover:underline"
                    >
                      设置用户名
                    </button>
                  )}
                  {user.username ? user.email : null}
                </p>
              )}
            </div>
          </div>
          <div className={`badge ${isPremium ? 'bg-peach text-cocoa' : 'bg-lavender/40 text-cocoa'}`}>
            {isPremium ? '🌾 田主会员' : '🌱 小萌新'}
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl bg-lavender/20 p-4">
            <p className="text-xs text-cocoa-light">会员状态</p>
            <p className="mt-1 text-lg font-semibold text-cocoa">{isPremium ? '已开通' : '未开通'}</p>
            {user.premiumUntil > 0 && (
              <p className="mt-0.5 text-xs text-cocoa-light">
                到期 {new Date(user.premiumUntil).toLocaleDateString('zh-CN')}
              </p>
            )}
          </div>
          <div className="rounded-2xl bg-mint/20 p-4">
            <p className="text-xs text-cocoa-light">用户 ID</p>
            <p className="mt-1 text-lg font-semibold text-cocoa">{user.id}</p>
          </div>
          <div className="rounded-2xl bg-peach/20 p-4">
            <p className="text-xs text-cocoa-light">{isPremium ? '本月导出次数' : '今日导出次数'}</p>
            <p className="mt-1 text-lg font-semibold text-cocoa">
              {isPremium ? '无限制' : `${user.exportsUsed || 0} / ${user.exportsLimit || 1}`}
            </p>
          </div>
          <div className="rounded-2xl bg-blush-light/40 p-4">
            <p className="text-xs text-cocoa-light">AI 生图额度</p>
            <p className="mt-1 text-lg font-semibold text-cocoa">{user.quota || 0}</p>
            <p className="mt-0.5 text-xs text-cocoa-light">
              {user.quotaExpiresAt
                ? `${new Date(user.quotaExpiresAt).toLocaleDateString('zh-CN')} 过期`
                : '购买额度包或签到可获得'}
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <a href="/pricing" className="btn-primary">
            购买 AI 额度
          </a>
          {!isPremium && (
            <span className="rounded-full bg-cream-200 px-4 py-2 text-sm font-medium text-cocoa-muted">
              卡密兑换会员
            </span>
          )}
          <button
            onClick={handleCheckin}
            disabled={checkinLoading || checkedInToday}
            className={`rounded-full px-5 py-2 font-semibold shadow-soft transition disabled:opacity-60 ${
              checkedInToday
                ? 'bg-cream-200 text-cocoa-muted'
                : 'bg-mint text-cocoa hover:bg-mint-dark hover:shadow-md'
            }`}
          >
            {checkinLoading ? '签到中…' : checkedInToday ? '今日已签到 ✨' : '每日签到 🌱'}
          </button>
          <button
            onClick={() => logout()}
            className="rounded-full border border-lavender/60 px-5 py-2 font-medium text-cocoa transition hover:bg-lavender/30"
          >
            登出
          </button>
        </div>
        {checkinMsg && <p className="mt-3 text-sm text-cocoa-light">{checkinMsg}</p>}
      </div>

      {/* 账号安全 */}
      <div className="card">
        <div className="mb-4 flex items-center gap-2">
          <LockKeyhole size={20} className="text-blush-dark" aria-hidden="true" />
          <h2 className="text-lg font-bold text-cocoa">修改密码</h2>
        </div>
        <form onSubmit={handlePasswordChange} className="grid gap-4 sm:grid-cols-2">
          <PasswordField
            id="current-password"
            label="当前密码"
            value={passwordForm.current}
            onChange={(event) => setPasswordForm((prev) => ({ ...prev, current: event.target.value }))}
            visible={passwordVisible.current}
            onToggle={() => setPasswordVisible((prev) => ({ ...prev, current: !prev.current }))}
            autoComplete="current-password"
          />
          <div className="hidden sm:block" aria-hidden="true" />
          <PasswordField
            id="new-password"
            label="新密码"
            value={passwordForm.next}
            onChange={(event) => setPasswordForm((prev) => ({ ...prev, next: event.target.value }))}
            visible={passwordVisible.next}
            onToggle={() => setPasswordVisible((prev) => ({ ...prev, next: !prev.next }))}
            autoComplete="new-password"
          />
          <PasswordField
            id="confirm-password"
            label="确认新密码"
            value={passwordForm.confirm}
            onChange={(event) => setPasswordForm((prev) => ({ ...prev, confirm: event.target.value }))}
            visible={passwordVisible.confirm}
            onToggle={() => setPasswordVisible((prev) => ({ ...prev, confirm: !prev.confirm }))}
            autoComplete="new-password"
          />
          <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
            <button type="submit" disabled={passwordSaving} className="btn-primary">
              {passwordSaving ? '更新中…' : '更新密码'}
            </button>
            <span className="text-xs text-cocoa-light">密码至少 6 个字符</span>
          </div>
          {(passwordError || passwordMessage) && (
            <p
              role="status"
              className={`text-sm sm:col-span-2 ${passwordError ? 'text-red-500' : 'text-mint-dark'}`}
            >
              {passwordError || passwordMessage}
            </p>
          )}
        </form>
      </div>

      {/* 卡密兑换 */}
      <div className="card">
        <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-cocoa">
          <span className="text-xl">🔑</span> 兑换卡密
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={activationCode}
            onChange={(e) => setActivationCode(formatActivationCode(e.target.value))}
            placeholder="XXXX-XXXX-XXXX-XXXX"
            className={`form-input h-10 flex-1 text-center text-sm uppercase tracking-widest ${activationCode && !isValidActivationCode(activationCode) ? 'border-red-300' : ''}`}
          />
          <button
            onClick={handleActivationRedeem}
            disabled={activationLoading || !isValidActivationCode(activationCode)}
            className={`rounded-full bg-blush px-5 py-2 text-sm font-semibold text-white transition hover:bg-blush-dark ${
              isValidActivationCode(activationCode) && !activationLoading ? 'shadow-soft' : 'cursor-not-allowed opacity-40'
            }`}
          >
            {activationLoading ? '兑换中…' : '兑换'}
          </button>
        </div>
        {activationMsg && (
          <p className={`mt-2 text-sm ${activationMsg.startsWith('✅') ? 'text-mint-dark' : 'text-red-500'}`}>
            {activationMsg}
          </p>
        )}
        <p className="mt-2 text-xs text-cocoa-light">
          支持会员卡（永久 / 周卡 / 天卡，时长自动叠加）和 AI 额度卡（5 次 / 10 次 / 30 次，兑换后额度立即到账，30 天内有效）。
        </p>
      </div>

      {/* 邀请好友（仅永久会员） */}
      {isPermanent && (
        <div className="card">
          <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-cocoa">
            <span className="text-xl">🎁</span> 邀请好友
          </h2>
          <p className="mb-3 text-xs text-cocoa-light">
            每成功邀请 1 位好友激活，你将获得 5 次 AI 生图额度，好友获得 7 天会员（最多邀请 {invites?.maxInvites ?? 3} 位）。
          </p>
          {invites && invites.codes.length > 0 && (
            <ul className="mb-3 space-y-2">
              {invites.codes.map((item) => (
                <li
                  key={item.code}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-cream-100 px-3 py-2"
                >
                  <span className="font-mono text-sm tracking-widest text-cocoa">{item.code}</span>
                  <span className="flex items-center gap-2 text-xs">
                    {item.isUsed ? (
                      <span className="text-mint-dark">已被 @{item.usedBy?.username || '好友'} 激活 ✨</span>
                    ) : (
                      <>
                        <span className="text-cocoa-light">待激活</span>
                        <button
                          onClick={() => handleInviteCopy(item.code)}
                          className="rounded-full bg-blush px-3 py-1 font-semibold text-white transition hover:bg-blush-dark"
                        >
                          复制
                        </button>
                      </>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleInviteCreate}
              disabled={inviteLoading || !invites || invites.remaining <= 0}
              className={`rounded-full bg-mint px-5 py-2 text-sm font-semibold text-cocoa transition hover:bg-mint-dark ${
                !inviteLoading && invites && invites.remaining > 0 ? 'shadow-soft' : 'cursor-not-allowed opacity-40'
              }`}
            >
              {inviteLoading
                ? '生成中…'
                : invites && invites.remaining > 0
                  ? `生成邀请码（剩余 ${invites.remaining} 次）`
                  : '邀请码已用完'}
            </button>
          </div>
          {inviteMsg && (
            <p className={`mt-2 text-sm ${inviteMsg.startsWith('✅') ? 'text-mint-dark' : 'text-red-500'}`}>
              {inviteMsg}
            </p>
          )}
        </div>
      )}

      {/* 我的图纸 */}
      <div className="card">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 text-lg font-bold text-cocoa">
            <span className="text-xl">🧩</span> 我的图纸
          </h2>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={redeemCode}
              onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
              maxLength={6}
              placeholder="输入分享码"
              className={`form-input h-9 w-28 text-center text-sm uppercase tracking-widest ${redeemCode && !isValidShareCode(redeemCode) ? 'border-red-300' : ''}`}
            />
            <button
              onClick={handleRedeemCode}
              disabled={redeemLoading || !isValidShareCode(redeemCode)}
              className={`rounded-full bg-blush px-4 py-2 text-sm font-semibold text-white transition hover:bg-blush-dark ${
                isValidShareCode(redeemCode) && !redeemLoading ? 'shadow-soft' : 'cursor-not-allowed opacity-40'
              }`}
            >
              {redeemLoading ? '…' : '查看'}
            </button>
          </div>
        </div>

        {patternsLoading ? (
          <p className="text-sm text-cocoa-light">加载中…</p>
        ) : patterns.length === 0 ? (
          <div className="rounded-2xl bg-cream-100 py-8 text-center">
            <p className="text-sm text-cocoa-light">还没有保存的图纸~</p>
            <Link to="/" className="mt-2 inline-block text-sm font-medium text-blush-dark hover:underline">
              去生成图纸 →
            </Link>
          </div>
        ) : (
          <>
          <div className="columns-2 gap-4 md:columns-3">
            {patterns.slice((patternsPage - 1) * GALLERY_PAGE_SIZE, patternsPage * GALLERY_PAGE_SIZE).map((item) => (
              <div
                key={item.id}
                className="mb-4 break-inside-avoid overflow-hidden rounded-2xl border border-lavender/30 bg-white shadow-sm transition hover:shadow-card"
              >
                <Link to={`/gallery/${item.id}`}>
                  <img src={item.imageUrl} alt={`pattern-${item.id}`} className="w-full" loading="lazy" />
                </Link>
                <div className="p-3">
                  {item.title && <p className="mb-1 text-sm font-medium text-cocoa">{item.title}</p>}
                  <div className="text-xs text-cocoa-light">
                    {item.brand && <span className="font-medium text-cocoa">{item.brand}</span>}
                    {item.brand && ' · '}
                    {item.width}×{item.height}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => togglePatternVisibility(item)}
                      className={`rounded-full px-2.5 py-1 text-[10px] font-semibold transition ${
                        item.isPublic
                          ? 'bg-blush text-white'
                          : 'border border-lavender/60 text-cocoa hover:bg-lavender/30'
                      }`}
                    >
                      {item.isPublic ? '公开' : '私有'}
                    </button>
                    <button
                      onClick={() => handleSharePattern(item)}
                      className="rounded-full bg-mint/40 px-2.5 py-1 text-[10px] font-semibold text-cocoa transition hover:bg-mint/60"
                    >
                      分享
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {patterns.length > GALLERY_PAGE_SIZE && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-xs text-cocoa-light">
                第 {patternsPage} 页 / 共 {Math.ceil(patterns.length / GALLERY_PAGE_SIZE)} 页（{patterns.length} 张）
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPatternsPage((p) => Math.max(1, p - 1))}
                  disabled={patternsPage <= 1}
                  className="rounded-full border border-lavender/60 px-3 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30 disabled:opacity-50"
                >
                  上一页
                </button>
                <button
                  onClick={() => setPatternsPage((p) => (p * GALLERY_PAGE_SIZE < patterns.length ? p + 1 : p))}
                  disabled={patternsPage * GALLERY_PAGE_SIZE >= patterns.length}
                  className="rounded-full border border-lavender/60 px-3 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30 disabled:opacity-50"
                >
                  下一页
                </button>
              </div>
            </div>
          )}
          </>
        )}
      </div>

      {shareCode && (
        <ShareCodeModal code={shareCode} onClose={() => setShareCode('')} />
      )}

      {/* 签到日历 */}
      <div className="card">
        <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-cocoa">
          <span className="text-xl">🌱</span> 签到日历
        </h2>
        <div className="flex items-center justify-between">
          <button
            onClick={() => changeCheckinMonth(-1)}
            className="rounded-full border border-lavender/60 px-3 py-1 text-sm text-cocoa transition hover:bg-lavender/30"
          >
            ← 上月
          </button>
          <span className="text-sm font-semibold text-cocoa">
            {checkinCalendarDate.getFullYear()} 年 {checkinCalendarDate.getMonth() + 1} 月
          </span>
          <button
            onClick={() => changeCheckinMonth(1)}
            className="rounded-full border border-lavender/60 px-3 py-1 text-sm text-cocoa transition hover:bg-lavender/30"
          >
            下月 →
          </button>
        </div>

        <div className="mt-4 grid grid-cols-7 gap-1 text-center text-xs text-cocoa-light">
          {['一', '二', '三', '四', '五', '六', '日'].map((d) => (
            <div key={d}>{d}</div>
          ))}
        </div>

        <div className="mt-1 grid grid-cols-7 gap-1">
          {Array.from({ length: getFirstWeekday(checkinCalendarDate.getFullYear(), checkinCalendarDate.getMonth() + 1) }).map(
            (_, i) => (
              <div key={`empty-${i}`} />
            )
          )}
          {Array.from({ length: getDaysInMonth(checkinCalendarDate.getFullYear(), checkinCalendarDate.getMonth() + 1) }).map(
            (_, i) => {
              const day = i + 1
              const year = checkinCalendarDate.getFullYear()
              const month = checkinCalendarDate.getMonth() + 1
              const dateStr = formatDateStr(year, month, day)
              const checked = checkinDates.includes(dateStr)
              const today = isSameDate(year, month, day)
              const past = isPastDate(year, month, day)
              return (
                <div
                  key={dateStr}
                  className={`flex aspect-square flex-col items-center justify-center rounded-xl text-xs font-medium transition ${
                    checked
                      ? 'bg-mint/30 text-mint-dark'
                      : past || today
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-cream-100 text-cocoa-light'
                  } ${today ? 'ring-2 ring-blush' : ''}`}
                >
                  <span>{day}</span>
                  <span className="mt-0.5 text-sm">{checked ? '🌱' : past || today ? '🥀' : ''}</span>
                </div>
              )
            }
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-3 text-xs text-cocoa-light">
          <span className="flex items-center gap-1">
            <span className="inline-flex h-4 w-4 items-center justify-center rounded bg-mint/30 text-[10px]">🌱</span>
            已签到
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-flex h-4 w-4 items-center justify-center rounded bg-yellow-100 text-[10px]">🥀</span>
            未签到
          </span>
        </div>
      </div>

      {/* 订单记录 */}
      <div className="card">
        <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-cocoa">
          <span className="text-xl">🧾</span> 订单记录
        </h2>
        {ordersLoading ? (
          <p className="text-sm text-cocoa-light">加载中…</p>
        ) : orders.length === 0 ? (
          <div className="rounded-2xl bg-cream-100 py-8 text-center">
            <p className="text-sm text-cocoa-light">还没有订单哦~</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-lavender/30">
            <div className="min-w-[600px]">
              <table className="w-full whitespace-nowrap text-left text-sm">
                <thead className="bg-cream-100 text-cocoa-light">
                  <tr>
                    <th className="px-4 py-3 font-semibold">订单号</th>
                    <th className="px-4 py-3 font-semibold">商品</th>
                    <th className="px-4 py-3 font-semibold">金额</th>
                    <th className="px-4 py-3 font-semibold">状态</th>
                    <th className="px-4 py-3 font-semibold">时间</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-lavender/20 bg-white">
                  {orders.map((o) => (
                    <tr key={o.id || o.orderId} className="transition hover:bg-cream-50">
                      <td className="px-4 py-3 font-mono text-xs text-cocoa">{(o.id || o.orderId)?.slice(0, 12)}…</td>
                      <td className="px-4 py-3 text-cocoa">{o.productId || o.productName || '-'}</td>
                      <td className="px-4 py-3 font-medium text-cocoa">¥{o.amount}</td>
                      <td className="px-4 py-3">{statusBadge(o.status)}</td>
                      <td className="px-4 py-3 text-cocoa-light">
                        {o.createdAt ? new Date(o.createdAt).toLocaleString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!ordersLoading && ordersTotal > ORDERS_PAGE_SIZE && (
          <div className="mt-4 flex items-center justify-between">
            <p className="text-xs text-cocoa-light">
              第 {ordersPage} 页 / 共 {Math.ceil(ordersTotal / ORDERS_PAGE_SIZE)} 页（{ordersTotal} 条）
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setOrdersPage((p) => Math.max(1, p - 1))}
                disabled={ordersPage <= 1}
                className="rounded-full border border-lavender/60 px-3 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30 disabled:opacity-50"
              >
                上一页
              </button>
              <button
                onClick={() => setOrdersPage((p) => (p * ORDERS_PAGE_SIZE < ordersTotal ? p + 1 : p))}
                disabled={ordersPage * ORDERS_PAGE_SIZE >= ordersTotal}
                className="rounded-full border border-lavender/60 px-3 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30 disabled:opacity-50"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>

      {/* AI 生成历史 */}
      <div className="card">
        <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-cocoa">
          <span className="text-xl">🎨</span> AI 生成历史
        </h2>
        {historyLoading ? (
          <p className="text-sm text-cocoa-light">加载中…</p>
        ) : history.length === 0 ? (
          <div className="rounded-2xl bg-cream-100 py-8 text-center">
            <p className="text-sm text-cocoa-light">还没有生成过图片哦~</p>
          </div>
        ) : (
          <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
            {history.slice((historyPage - 1) * GALLERY_PAGE_SIZE, historyPage * GALLERY_PAGE_SIZE).map((item) => (
              <a
                key={item.taskId}
                href={item.imageUrl || '#'}
                target="_blank"
                rel="noreferrer"
                className="group overflow-hidden rounded-2xl border border-lavender/30 bg-white shadow-sm transition hover:shadow-md"
              >
                <div className="aspect-square bg-cream-100">
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt={item.prompt}
                      className="h-full w-full object-cover transition group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-2xl text-cocoa-light">🖼️</div>
                  )}
                </div>
                <div className="p-3">
                  <p className="line-clamp-2 text-xs text-cocoa" title={item.prompt}>
                    {item.prompt}
                  </p>
                  <p className="mt-1 text-[10px] text-cocoa-light">
                    {item.status === 'completed' ? '已完成' : item.status === 'failed' ? '失败' : '生成中…'} ·{' '}
                    {item.createdAt ? new Date(item.createdAt).toLocaleString() : '-'}
                  </p>
                </div>
              </a>
            ))}
          </div>
          {history.length > GALLERY_PAGE_SIZE && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-xs text-cocoa-light">
                第 {historyPage} 页 / 共 {Math.ceil(history.length / GALLERY_PAGE_SIZE)} 页（{history.length} 条）
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                  disabled={historyPage <= 1}
                  className="rounded-full border border-lavender/60 px-3 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30 disabled:opacity-50"
                >
                  上一页
                </button>
                <button
                  onClick={() => setHistoryPage((p) => (p * GALLERY_PAGE_SIZE < history.length ? p + 1 : p))}
                  disabled={historyPage * GALLERY_PAGE_SIZE >= history.length}
                  className="rounded-full border border-lavender/60 px-3 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30 disabled:opacity-50"
                >
                  下一页
                </button>
              </div>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  )
}
